import { Component } from '@theme/component';

/**
 * TRB share button.
 *
 * Uses the native Web Share API (mobile/supported browsers); otherwise copies the
 * product URL to the clipboard and shows a brief "Copied" confirmation. No social
 * widget, no external library (CLAUDE.md). Pure progressive enhancement — without
 * JS the button simply does nothing harmful (it has no href to break).
 *
 * @typedef {object} Refs
 * @property {HTMLElement} feedback - Inline live-region for the "Copied" message.
 *
 * @extends Component<Refs>
 */
class TrbShare extends Component {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;

  /** Declarative handler: `on:click="/share"`. */
  async share() {
    const url = this.dataset.shareUrl || window.location.href;
    const title = this.dataset.shareTitle || document.title;

    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch (error) {
        // User cancelled the share sheet — nothing to do.
        if (error instanceof Error && error.name === 'AbortError') return;
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      this.#flash('Copied');
    } catch {
      this.#flash('Copy failed');
    }
  }

  /** @param {string} message */
  #flash(message) {
    const { feedback } = this.refs;
    if (!feedback) return;

    feedback.textContent = message;
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => {
      feedback.textContent = '';
    }, 2000);
  }
}

if (!customElements.get('trb-share')) {
  customElements.define('trb-share', TrbShare);
}
